import { useEffect, type RefObject } from 'react'

// Subtle parallax: translate an element a little slower than scroll.
export function useParallax(ref: RefObject<HTMLElement | null>, factor = 0.12) {
  useEffect(() => {
    let raf = 0
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        if (ref.current) {
          const y = window.scrollY * factor
          ref.current.style.transform = `translate3d(0, ${y}px, 0)`
        }
      })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
    }
  }, [ref, factor])
}

// Reveal-on-scroll: adds `.in` to every `.pd-reveal` as it enters the viewport.
export function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll<HTMLElement>('.pd-reveal')
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in')
            io.unobserve(e.target)
          }
        })
      },
      { threshold: 0.16 },
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])
}
